import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { StatusBadge } from '../../../../../components/atoms/StatusBadge';

interface StatusOption {
  code: string;
  name: string;
}

export interface StatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  currentStatus: string;
  possibleStatuses: StatusOption[];
  preferredStatuses: StatusOption[];
  loading: boolean;
  onSubmit: (statusCode: string, notes: string) => void;
  submitting: boolean;
}

export const StatusChangeDialog: React.FC<StatusChangeDialogProps> = ({
  open,
  onClose,
  currentStatus,
  possibleStatuses,
  preferredStatuses,
  loading,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedStatus('');
      setNotes('');
    }
  }, [open]);

  const allStatuses = [
    ...preferredStatuses.map((s) => ({ ...s, preferred: true })),
    ...possibleStatuses.filter((s) => !preferredStatuses.some((p) => p.code === s.code)).map((s) => ({ ...s, preferred: false })),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Change Status
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}>Current Status</Typography>
          <StatusBadge status={currentStatus} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.75 }}>Select New Status</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {allStatuses.map((status) => (
                <Box
                  key={status.code}
                  onClick={() => setSelectedStatus(status.code)}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: selectedStatus === status.code ? theme.palette.v4.accent : theme.palette.v4.border,
                    bgcolor: selectedStatus === status.code ? `${theme.palette.v4.accent}10` : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { borderColor: theme.palette.v4.accent },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  <StatusBadge status={status.name} size="small" />
                  {status.preferred && (
                    <Typography sx={{ fontSize: '9px', color: theme.palette.v4.accent, fontWeight: 600 }}>Preferred</Typography>
                  )}
                </Box>
              ))}
              {allStatuses.length === 0 && (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>No available status transitions</Typography>
              )}
            </Box>

            <TextField
              label="Notes (optional)"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(selectedStatus, notes)}
          disabled={!selectedStatus || submitting}
          sx={{ textTransform: 'none', bgcolor: theme.palette.v4.accent, '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 } }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Change Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
